import type { ReactNode } from 'react';

/**
 * The little Markdown the model writes (paragraphs, bullet and numbered lists, one
 * level of nesting, **bold**, headings), rendered as React elements: no HTML from the
 * model ever reaches the page, and no library is needed.
 */
export function Markdown({ text }: { text: string }): ReactNode {
  return <>{blocks(text)}</>;
}

type Item = { text: string; children: string[] };

function blocks(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) {
      i++;
      continue;
    }
    const list = listKind(line);
    if (list) {
      const items: Item[] = [];
      while (i < lines.length) {
        const l = lines[i]!;
        if (!l.trim()) {
          // A blank line ends the list unless more items follow
          if (i + 1 < lines.length && listKind(lines[i + 1]!)) {
            i++;
            continue;
          }
          break;
        }
        const indented = /^\s{2,}/.test(l);
        if (indented && items.length > 0) {
          items.at(-1)!.children.push(stripMarker(l.trim()));
        } else if (listKind(l)) {
          items.push({ text: stripMarker(l.trim()), children: [] });
        } else if (items.length > 0) {
          items.at(-1)!.text += ` ${l.trim()}`;
        } else break;
        i++;
      }
      const Tag = list === 'ol' ? 'ol' : 'ul';
      out.push(
        <Tag
          key={out.length}
          className={`my-2 space-y-1 ps-5 ${list === 'ol' ? 'list-decimal' : 'list-disc'}`}
        >
          {items.map((item, k) => (
            <li key={k}>
              {inline(item.text)}
              {item.children.length > 0 && (
                <ul className="mt-1 list-[circle] space-y-0.5 ps-5">
                  {item.children.map((c, m) => (
                    <li key={m}>{inline(c)}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </Tag>,
      );
      continue;
    }
    const heading = /^#{1,6}\s+(.*)$/.exec(line);
    if (heading) {
      out.push(
        <p key={out.length} className="mt-3 mb-1 font-semibold">
          {inline(heading[1]!)}
        </p>,
      );
      i++;
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i]!.trim() && !listKind(lines[i]!)) {
      para.push(lines[i]!.trim());
      i++;
    }
    out.push(
      <p key={out.length} className="my-2 first:mt-0 last:mb-0">
        {para.flatMap((p, k) => (k === 0 ? [inline(p)] : [<br key={`br${k}`} />, inline(p)]))}
      </p>,
    );
  }
  return out;
}

function listKind(line: string): 'ul' | 'ol' | null {
  const t = line.trimStart();
  if (/^[-*•]\s+/.test(t)) return 'ul';
  if (/^\d+[.)]\s+/.test(t)) return 'ol';
  return null;
}

const stripMarker = (l: string) => l.replace(/^([-*•]|\d+[.)])\s+/, '');

/** **bold** and *italic*; everything else is plain text. */
function inline(text: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*|\*[^*\s][^*]*\*)/g).map((part, k) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) return <strong key={k}>{part.slice(2, -2)}</strong>;
    if (/^\*[^*\s][^*]*\*$/.test(part)) return <em key={k}>{part.slice(1, -1)}</em>;
    return part;
  });
}
